import { Label, Input, Textarea } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Sparkles } from 'lucide-react'
import type { WorldBuilderInput } from '@creanizer/types'

interface UniqueElementsFormProps {
  input: WorldBuilderInput
  onChange: (input: WorldBuilderInput) => void
}

export function UniqueElementsForm({ input, onChange }: UniqueElementsFormProps) {
  const handlePhysicsLawsChange = (value: string) => {
    onChange({
      ...input,
      uniqueElements: {
        ...input.uniqueElements,
        physicsLaws: value.split('\n').filter(Boolean),
      },
    })
  }
  
  const handleConceptsChange = (value: string) => {
    onChange({
      ...input,
      uniqueElements: {
        ...input.uniqueElements,
        uniqueConcepts: value.split('\n').filter(Boolean),
      },
    })
  }
  
  const handleTaboosChange = (value: string) => {
    onChange({
      ...input,
      uniqueElements: {
        ...input.uniqueElements,
        tabooSubjects: value.split('\n').filter(Boolean),
      },
    })
  }
  
  const handleResourcesChange = (value: string) => {
    onChange({
      ...input,
      uniqueElements: {
        ...input.uniqueElements,
        uniqueResources: value.split('\n').filter(Boolean),
      },
    })
  }
  
  const handleImpossibilitiesChange = (value: string) => {
    onChange({
      ...input,
      uniqueElements: {
        ...input.uniqueElements,
        impossibilities: value.split('\n').filter(Boolean),
      },
    })
  }
  
  return (
    <div className="space-y-4">
      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertDescription>
          この世界独自の要素を設定して、他にないユニークな世界観を作りましょう。
        </AlertDescription>
      </Alert>
      
      <div>
        <Label htmlFor="physicsLaws">物理法則のカスタマイズ（各行に1つ）</Label>
        <Textarea
          id="physicsLaws"
          value={input.uniqueElements?.physicsLaws?.join('\n') || ''}
          onChange={(e) => handlePhysicsLawsChange(e.target.value)}
          placeholder="例:&#10;重力が逆方向に働く&#10;時間の流れが不規則&#10;思考が物質に影響を与える&#10;エネルギー保存則が成立しない"
          rows={4}
        />
        <p className="text-xs text-muted-foreground mt-1">
          この世界特有の物理法則を自由に設定できます
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="dominantSpecies">支配種族</Label>
          <Input
            id="dominantSpecies"
            value={input.uniqueElements?.dominantSpecies || ''}
            onChange={(e) => onChange({
              ...input,
              uniqueElements: {
                ...input.uniqueElements,
                dominantSpecies: e.target.value,
              },
            })}
            placeholder="例: ドラゴン、機械生命体"
          />
        </div>
        
        <div>
          <Label htmlFor="powerSource">力の源泉</Label>
          <Input
            id="powerSource"
            value={input.uniqueElements?.powerSource || ''}
            onChange={(e) => onChange({
              ...input,
              uniqueElements: {
                ...input.uniqueElements,
                powerSource: e.target.value,
              },
            })}
            placeholder="例: マナ、太陽エネルギー"
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="worldEndingThreat">世界を脅かす存在</Label>
        <Input
          id="worldEndingThreat"
          value={input.uniqueElements?.worldEndingThreat || ''}
          onChange={(e) => onChange({
            ...input,
            uniqueElements: {
              ...input.uniqueElements,
              worldEndingThreat: e.target.value,
            },
          })}
          placeholder="例: 古代の邪神、次元の崩壊"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="uniqueConcepts">独自概念（各行に1つ）</Label>
          <Textarea
            id="uniqueConcepts"
            value={input.uniqueElements?.uniqueConcepts?.join('\n') || ''}
            onChange={(e) => handleConceptsChange(e.target.value)}
            placeholder="例:&#10;魂の結晶化&#10;記憶の売買&#10;夢の具現化"
            rows={3}
          />
        </div>
        
        <div>
          <Label htmlFor="tabooSubjects">タブー・禁忌（各行に1つ）</Label>
          <Textarea
            id="tabooSubjects"
            value={input.uniqueElements?.tabooSubjects?.join('\n') || ''}
            onChange={(e) => handleTaboosChange(e.target.value)}
            placeholder="例:&#10;死者の名を呼ぶ&#10;鏡を見る&#10;夜に口笛を吹く"
            rows={3}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="uniqueResources">独自資源（各行に1つ）</Label>
          <Textarea
            id="uniqueResources"
            value={input.uniqueElements?.uniqueResources?.join('\n') || ''}
            onChange={(e) => handleResourcesChange(e.target.value)}
            placeholder="例:&#10;液体魔力&#10;時間結晶&#10;感情エネルギー"
            rows={3}
          />
        </div>
        
        <div>
          <Label htmlFor="impossibilities">不可能なこと（各行に1つ）</Label>
          <Textarea
            id="impossibilities"
            value={input.uniqueElements?.impossibilities?.join('\n') || ''}
            onChange={(e) => handleImpossibilitiesChange(e.target.value)}
            placeholder="例:&#10;嘘をつく&#10;火を使う&#10;金属を作る"
            rows={3}
          />
        </div>
      </div>
    </div>
  )
}